import {
  BUFFER_MS,
  DUMMY_PROGRESS_INTERVAL_MS,
} from '@/utilities/constants/clientTimeouts';
import { Dispatch, useCallback, useEffect, useRef } from 'react';

let _testInitValue: number | null = null;

export const _testInit = (value: number) => {
  _testInitValue = value;
};

/*
┌─────────────────────────────────────────────────────────────┐
│  useSyncProgress (LoadingBar)                               │
│  - Animates progress bar visually                           │
│  - Nudges forward even when no real progress                │
│  - Updates internalLoadProgress in store                    │
└─────────────────────────────────────────────────────────────┘
*/
export const useProgressSync = (
  setDisplayProgress: Dispatch<React.SetStateAction<number>>,
) => {
  const dummyTimerRef = useRef<number | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const finishTimerRef = useRef<number | null>(null);
  const lastEmitRef = useRef<number | null>(null);
  const lastReportedRef = useRef<number>(0); // track last reported progress
  const pendingFractionRef = useRef<number | null>(null);

  const clearAll = useCallback(() => {
    if (dummyTimerRef.current != null) {
      clearInterval(dummyTimerRef.current);
      dummyTimerRef.current = null;
    }
    if (idleTimerRef.current != null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (finishTimerRef.current != null) {
      clearTimeout(finishTimerRef.current);
      finishTimerRef.current = null;
    }
  }, []);

  const stopDummy = useCallback(() => {
    if (dummyTimerRef.current != null) {
      clearInterval(dummyTimerRef.current);
      dummyTimerRef.current = null;
    }
  }, []);

  // keep fractional state and replace any running dummy
  const startDummy = useCallback(
    (tickMs = 100, step = 1, max = 90) => {
      if (dummyTimerRef.current != null) {
        clearInterval(dummyTimerRef.current);
        dummyTimerRef.current = null;
      }
      pendingFractionRef.current = null;
      dummyTimerRef.current = window.setInterval(() => {
        setDisplayProgress((p) => {
          const base = pendingFractionRef.current ?? p;
          const next = Math.min(max, +(base + step).toFixed(3)); // preserve fractions
          // Slow nudges use fractional steps — batch until the rounded percent changes.
          if (step < 1 && Math.round(next) === Math.round(p)) {
            pendingFractionRef.current = next;
            return p;
          }
          pendingFractionRef.current = null;
          return next;
        });
      }, tickMs);
    },
    [setDisplayProgress],
  );

  const scheduleIdle = useCallback(() => {
    if (idleTimerRef.current != null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    idleTimerRef.current = window.setTimeout(() => {
      const since = lastEmitRef.current
        ? Date.now() - lastEmitRef.current
        : Infinity;
      // require at least one full DUMMY interval of silence before nudging
      if (since >= DUMMY_PROGRESS_INTERVAL_MS) {
        // gentle nudges while waiting for next real emit — use slower tick + small step
        startDummy(DUMMY_PROGRESS_INTERVAL_MS / 2, 0.05, 100);
      }
      idleTimerRef.current = null;
    }, 100);
  }, [startDummy]);

  const markEmit = useCallback(() => {
    lastEmitRef.current = Date.now();
    if (idleTimerRef.current != null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const applyGameProgress = useCallback(
    (reported: number) => {
      // normalize and remember last reported
      const r = Math.max(0, Math.min(100, reported));
      lastReportedRef.current = r;
      markEmit();

      // if emitter is ahead => stop dummy and let emitter control
      setDisplayProgress((prev) => {
        if (r > prev) {
          stopDummy();
          pendingFractionRef.current = null;
          // schedule idle in case emitter stops later
          scheduleIdle();
          return r;
        }

        // if emitter is behind or equal, do NOT stop dummy — instead start gentle slow nudges
        // ensure dummy is running in slow mode so reported can catch up
        startDummy(DUMMY_PROGRESS_INTERVAL_MS * 2, 0.05, 100);
        return prev; // do not lower display
      });
    },
    [markEmit, scheduleIdle, startDummy, stopDummy, setDisplayProgress],
  );

  const finishWithCallback = useCallback(
    (cb?: () => void) => {
      stopDummy();
      if (finishTimerRef.current != null) {
        clearTimeout(finishTimerRef.current);
        finishTimerRef.current = null;
      }
      finishTimerRef.current = window.setTimeout(() => {
        cb?.();
        finishTimerRef.current = null;
      }, BUFFER_MS);
    },
    [stopDummy],
  );

  // Complete progress to 100% and call callback - uses microtask to avoid synchronous setState in effect
  const completeProgress = useCallback(
    (cb?: () => void) => {
      stopDummy();
      // Use queueMicrotask to defer state update, avoiding synchronous setState in effect body
      queueMicrotask(() => {
        setDisplayProgress(100);
      });
      finishWithCallback(cb);
    },
    [stopDummy, setDisplayProgress, finishWithCallback],
  );

  const cleanup = useCallback(() => {
    clearAll();
  }, [clearAll]);

  useEffect(() => {
    if (_testInitValue !== null) {
      setDisplayProgress(_testInitValue);
      _testInitValue = null; // reset after first use
    }
  }, [setDisplayProgress]);

  return {
    startDummy,
    stopDummy,
    applyGameProgress,
    scheduleIdle,
    markEmit,
    finishWithCallback,
    completeProgress,
    cleanup,
    _testInit,
  };
};

export type DummyTimeoutProps = {
  tickMs: number;
  step: number;
  max: number;
};
